/**
 * User Preferences API
 * Professional View toggle and settings
 */

import { apiClient } from "./client";
import type {
  ProfessionalViewResponse,
  ProfessionalViewUpdate,
  UserPreferencesResponse,
  UserPreferencesUpdate,
} from "./types";

/**
 * Get Professional View status
 * - Standard View: Hourly intervals (24 points/day)
 * - Professional View: 15-minute intervals (96 points/day)
 */
export async function getProfessionalView(): Promise<ProfessionalViewResponse> {
  return apiClient.get<ProfessionalViewResponse>("/api/v1/preferences/professional-view");
}

/**
 * Set Professional View toggle
 * Affects: Volume Forecast, EVI, Maintenance Planning, CMS
 */
export async function setProfessionalView(
  enabled: boolean
): Promise<ProfessionalViewResponse> {
  const update: ProfessionalViewUpdate = {
    professional_view: enabled,
  };
  return apiClient.put<ProfessionalViewResponse>(
    "/api/v1/preferences/professional-view",
    update
  );
}

/**
 * Get all user preferences
 */
export async function getUserPreferences(): Promise<UserPreferencesResponse> {
  return apiClient.get<UserPreferencesResponse>("/api/v1/preferences");
}

/**
 * Update user preferences (partial update)
 */
export async function updateUserPreferences(
  update: UserPreferencesUpdate
): Promise<UserPreferencesResponse> {
  return apiClient.patch<UserPreferencesResponse>("/api/v1/preferences", update);
}

/**
 * Get data granularity based on professional view setting
 */
export function getGranularity(isProfessional: boolean): {
  intervalMinutes: number;
  pointsPerDay: number;
  label: string;
} {
  if (isProfessional) {
    return {
      intervalMinutes: 15,
      pointsPerDay: 96,
      label: "15-minute intervals",
    };
  }
  return {
    intervalMinutes: 60,
    pointsPerDay: 24,
    label: "Hourly intervals",
  };
}
